import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AdminPage, Field, Notice, OnOffBadge, SkeletonList, SkeletonTable, Switch } from '@/admin/AdminShell'
import { Icon } from '@/components/Icon'
import { RecordPicker } from '@/card/RecordPicker'
import { StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  fetchTelephonyEvents,
  fetchTelephonySettings,
  updateTelephonySettings,
  type TelephonyExtension,
  type TelephonySettings,
} from './api'

/**
 * Телефония в администрировании (тикет #122): подключение Mango Office, сопоставление
 * добавочных номеров сотрудникам и журнал событий АТС.
 *
 * Ключ (`vpbx_api_key`) и соль подписи (`vpbx_api_salt`) только записываются: сервер отвечает,
 * заданы ли они, но не какие. Пустое поле оставляет сохранённое значение.
 *
 * Адрес для АТС показывается целиком — его вставляют в личный кабинет Mango («Адрес внешней
 * системы»), и без него ни одно событие звонка до CRM не дойдёт. Журнал событий показывает и
 * отклонённые по подписи: подделка и разъехавшаяся соль снаружи выглядят одинаково.
 */
export function TelephonySettingsPage() {
  const { t, user } = useApp()
  const mayRead = can(user, 'system.integration', 'read')
  return (
    <AdminPage titleKey="telephony.adm.title" leadKey="telephony.adm.lead" forbidden={!mayRead}
               readOnly={!can(user, 'system.integration', 'update')}>
      {!mayRead ? (
        <StateView boxed kind="forbidden" title={t('telephony.adm.forbidden')} />
      ) : (
        <div className="stack" style={{ gap: 20 }}>
          <ConnectionPanel />
          <EventsPanel />
        </div>
      )}
    </AdminPage>
  )
}

function ConnectionPanel() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const mayEdit = can(user, 'system.integration', 'update')
  const settings = useQuery({ queryKey: ['admin-telephony'], queryFn: ({ signal }) => fetchTelephonySettings(signal) })
  const [draft, setDraft] = useState<TelephonySettings | null>(null)
  const [extensions, setExtensions] = useState<TelephonyExtension[]>([])
  const [apiKey, setApiKey] = useState('')
  const [salt, setSalt] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(null)
  const [newExtension, setNewExtension] = useState('')

  useEffect(() => {
    if (settings.data) {
      setDraft(settings.data)
      setExtensions(settings.data.extensions ?? [])
    }
  }, [settings.data])

  const save = useMutation({
    mutationFn: () =>
      updateTelephonySettings({
        enabled: draft!.enabled,
        endpoint: draft!.endpoint?.trim() || null,
        apiKey: apiKey === '' ? undefined : apiKey,
        salt: salt === '' ? undefined : salt,
        extensions: Object.fromEntries(extensions.map((row) => [row.employeeId, row.extension])),
        lineNumber: draft!.lineNumber?.trim() || null,
      }),
    onSuccess: (value) => {
      setNotice(null)
      setApiKey('')
      setSalt('')
      setSaved(true)
      queryClient.setQueryData(['admin-telephony'], value)
      void queryClient.invalidateQueries({ queryKey: ['telephony-status'] })
    },
    onError: (failure) => {
      setSaved(false)
      setNotice(failure instanceof ApiError ? failure.message : t('app.error'))
    },
  })

  if (settings.error) return <StateView kind="error" compact title={t('telephony.adm.error')} />
  if (!draft) return <SkeletonList rows={4} />

  const set = (patch: Partial<TelephonySettings>) => {
    setSaved(false)
    setDraft({ ...draft, ...patch })
  }
  const webhook = `${window.location.origin}${draft.webhookPath}`
  const id = 'tel'

  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={t('telephony.adm.connection')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('telephony.adm.connection')}</span>
        <OnOffBadge on={settings.data?.enabled ?? false} />
      </div>
      {settings.data?.problem && (
        <div className="alert alert-warning" role="note">
          <Icon name="alert" />
          <span>{settings.data.problem}</span>
        </div>
      )}
      <div className="alert alert-info" role="note">
        <Icon name="phone" />
        <span>
          {t('telephony.adm.webhook')} <code className="mono">{webhook}</code>
        </span>
      </div>
      <form className="stack" style={{ gap: 12 }} aria-label={t('telephony.adm.connection')} onSubmit={(event) => {
        event.preventDefault()
        if (mayEdit) save.mutate()
      }}>
        <Switch checked={draft.enabled} disabled={!mayEdit} label={t('telephony.adm.enabled')} onChange={(value) => set({ enabled: value })} />
        <div className="form-grid">
          <Field label={t('telephony.adm.endpoint')} htmlFor={`${id}-endpoint`} help={t('telephony.adm.endpoint.help')}>
            <input id={`${id}-endpoint`} className="input mono" disabled={!mayEdit} value={draft.endpoint ?? ''}
                   onChange={(e) => set({ endpoint: e.target.value })} />
          </Field>
          <Field label={t('telephony.adm.line')} htmlFor={`${id}-line`} help={t('telephony.adm.line.help')}>
            <input id={`${id}-line`} className="input mono" disabled={!mayEdit} value={draft.lineNumber ?? ''}
                   onChange={(e) => set({ lineNumber: e.target.value })} />
          </Field>
          <Field label={t('telephony.adm.apiKey')} htmlFor={`${id}-key`}
                 help={draft.apiKeySet ? t('telephony.adm.secret.set') : t('telephony.adm.secret.unset')}>
            <input id={`${id}-key`} className="input" type="password" autoComplete="new-password" disabled={!mayEdit}
                   value={apiKey} onChange={(e) => { setSaved(false); setApiKey(e.target.value) }} />
          </Field>
          <Field label={t('telephony.adm.salt')} htmlFor={`${id}-salt`}
                 help={draft.saltSet ? t('telephony.adm.secret.set') : t('telephony.adm.secret.unset')}>
            <input id={`${id}-salt`} className="input" type="password" autoComplete="new-password" disabled={!mayEdit}
                   value={salt} onChange={(e) => { setSaved(false); setSalt(e.target.value) }} />
          </Field>
        </div>

        <span className="h3">{t('telephony.adm.extensions')}</span>
        <p className="t-sm muted">{t('telephony.adm.extensions.help')}</p>
        {extensions.length === 0 ? (
          <p className="muted">{t('telephony.adm.extensions.empty')}</p>
        ) : (
          <table className="card-table" aria-label={t('telephony.adm.extensions')}>
            <thead>
              <tr>
                <th>{t('telephony.adm.employee')}</th>
                <th>{t('telephony.adm.extension')}</th>
                {mayEdit && <th aria-label={t('telephony.adm.remove')} />}
              </tr>
            </thead>
            <tbody>
              {extensions.map((row) => (
                <tr key={row.employeeId}>
                  <td>{row.employeeName ?? row.employeeId}</td>
                  <td>
                    <input className="input mono" aria-label={t('telephony.adm.extension')} disabled={!mayEdit} value={row.extension}
                           onChange={(e) => {
                             setSaved(false)
                             setExtensions(extensions.map((other) => other.employeeId === row.employeeId ? { ...other, extension: e.target.value } : other))
                           }} />
                  </td>
                  {mayEdit && (
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
                        setSaved(false)
                        setExtensions(extensions.filter((other) => other.employeeId !== row.employeeId))
                      }}>
                        {t('telephony.adm.remove')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {mayEdit && (
          <div className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label={t('telephony.adm.employee')} htmlFor={`${id}-employee`}>
              <RecordPicker id={`${id}-employee`} entity="hr.employee" searchColumn="last_name" extraColumn="personnel_number"
                            value={picked} onChange={setPicked} />
            </Field>
            <Field label={t('telephony.adm.extension')} htmlFor={`${id}-new-ext`}>
              <input id={`${id}-new-ext`} className="input mono" value={newExtension} onChange={(e) => setNewExtension(e.target.value)} />
            </Field>
            <button type="button" className="btn btn-secondary" disabled={picked == null || newExtension.trim() === ''}
                    onClick={() => {
                      if (picked == null) return
                      setSaved(false)
                      setExtensions([
                        ...extensions.filter((row) => row.employeeId !== picked.id),
                        { employeeId: picked.id, employeeName: picked.label, extension: newExtension.trim() },
                      ])
                      setPicked(null)
                      setNewExtension('')
                    }}>
              {t('telephony.adm.add')}
            </button>
          </div>
        )}

        <Notice text={notice} />
        {saved && <span className="t-sm muted" role="status">{t('telephony.adm.saved')}</span>}
        {mayEdit && (
          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? t('adm.saving') : t('admin.save')}
            </button>
          </div>
        )}
      </form>
    </section>
  )
}

function EventsPanel() {
  const { t, locale } = useApp()
  const events = useQuery({ queryKey: ['admin-telephony-events'], queryFn: ({ signal }) => fetchTelephonyEvents(signal) })
  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={t('telephony.adm.events')}>
      <span className="h2">{t('telephony.adm.events')}</span>
      {events.isLoading ? (
        <SkeletonTable columns={4} rows={4} />
      ) : events.error ? (
        <StateView kind="error" compact title={t('telephony.adm.error')} />
      ) : (events.data ?? []).length === 0 ? (
        <p className="muted">{t('telephony.adm.events.empty')}</p>
      ) : (
        <table className="card-table" aria-label={t('telephony.adm.events')}>
          <thead>
            <tr>
              <th>{t('telephony.adm.events.at')}</th>
              <th>{t('telephony.adm.events.type')}</th>
              <th>{t('telephony.adm.events.status')}</th>
              <th>{t('telephony.adm.events.detail')}</th>
            </tr>
          </thead>
          <tbody>
            {(events.data ?? []).map((event) => (
              <tr key={event.id}>
                <td>{new Date(event.receivedAt).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')}</td>
                <td className="mono">{event.eventType}</td>
                <td>
                  <span className={`badge${event.status === 'rejected' || event.status === 'failed' ? ' b-danger' : ''}`}>
                    {t(`telephony.event.${event.status}`)}
                  </span>
                </td>
                <td className="t-sm">{event.detail ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
